import { PerimeterEnergyTrajectoryService } from './../../../../@core/utils/perimeter-energy-trajectory.service';
import { UsersService } from './../../../../@core/utils/users.service';
import { AsyncPipe, DatePipe } from '@angular/common';
import { ReferenceDataService } from './../../../../@core/utils/reference-data.service';
import { SaveToastService } from './../../../../@core/utils/save-toast.service';
import { ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { KPI } from '../../../../@core/api';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, switchMap } from 'rxjs';
import { debounceTime, filter, first, map, takeUntil, tap } from 'rxjs/operators';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { I18nService, PerimetersService, ScopeService } from '../../../../@core/utils';
import { IdMap, idMapToArray } from '../../../../@core/utils/scope.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UsernamePipe } from '../../../pipes';
import { NbAccordionModule, NbButtonModule, NbInputModule, NbTooltipModule } from '@nebular/theme';
import { DisplayedPerimeterReferenceValue, RefDataInputComponent } from '../../ref-data-input/ref-data-input.component';
import { CircularSpinnerComponent } from '../../circular-spinner/circular-spinner.component';

export interface PerimeterWithRefData {
  perimeter: KPI.Perimeter,
  refData: DisplayedPerimeterReferenceValue[],
  children: PerimeterWithRefData[],
}

@Component({
  selector: 'app-site-reference-datas',
  templateUrl: './site-reference-datas.component.html',
  styleUrls: ['./site-reference-datas.component.scss'],
  standalone: true,
  imports: [
    TranslateModule,
    NbAccordionModule,
    FormsModule,
    NbTooltipModule,
    ReactiveFormsModule,
    AsyncPipe,
    NbInputModule,
    RefDataInputComponent,
    NbButtonModule,
    CircularSpinnerComponent,
  ],
})
export class SiteReferenceDatasComponent implements OnInit, OnChanges, OnDestroy {

  @Input() perimeter: KPI.Perimeter;
  @Input() focusedPerimeterId: number | null = null;
  @Input() refreshKey: number = 0;

  public form: UntypedFormGroup = new UntypedFormGroup({});

  public loading: boolean = false;

  private isSaving: boolean = false;
  private autosaveSubscription: Subscription;
  private destroy$ = new Subject<void>();

  private initialFormValues$ = new BehaviorSubject<any>(null);

  public anyValueChangedFromInitial$: Observable<boolean> = combineLatest([
    this.form.valueChanges,
    this.initialFormValues$.pipe(filter((initialFormValues) => !!initialFormValues)),
  ]).pipe(
    map(([formValue, initialFormValues]) => !!this.getChangedValues(formValue, initialFormValues)?.length),
  );

  public refDataByPerimeter$: Observable<PerimeterWithRefData[]>;

  constructor(
    private scopeService: ScopeService,
    private referenceDataService: ReferenceDataService,
    private translate: TranslateService,
    private i18nService: I18nService,
    private usersService: UsersService,
    private trajectoryService: PerimeterEnergyTrajectoryService,
    private cdr: ChangeDetectorRef,
    private saveToast: SaveToastService,
    private perimetersService: PerimetersService,
  ) { }

  public ngOnInit(): void {
    this.setUpRefDataByPerimeter();
    this.anyValueChangedFromInitial$.subscribe((hasChanged) => {
      this.referenceDataService.setValueChanged(hasChanged);
    });

    this.perimetersService._perimeterDataRefresh$.pipe(
      filter(event => event.perimeterId === this.perimeter.id),
      takeUntil(this.destroy$),
    ).subscribe(({ data }) => {
      if (data.name !== undefined) {
        this.perimeter.name = data.name;
      }
      if (data.sub_perimeters) {
        const updatedById = new Map<number, KPI.Perimeter>(
          (data.sub_perimeters as KPI.Perimeter[]).map(sp => [sp.id, sp])
        );
        (this.perimeter.sub_perimeters || []).forEach(sp => {
          const updated = updatedById.get(sp.id);
          if (!updated) return;
          if (updated.name !== undefined) sp.name = updated.name;
          if (updated.flooring_area !== undefined) sp.flooring_area = updated.flooring_area;
          if (updated.perimeter_type !== undefined) sp.perimeter_type = updated.perimeter_type;
          if (updated.creation_year !== undefined) sp.creation_year = updated.creation_year;
        });
      }
      this.setUpRefDataByPerimeter();
    });

    this.autosaveSubscription = this.form.valueChanges.pipe(
      debounceTime(1500),
    ).subscribe((formValue) => {
      if (this.isSaving || !this.initialFormValues$.value) {
        return;
      }
      const changedValues = this.getChangedValues(formValue, this.initialFormValues$.value);
      const hasRefDataChanges = changedValues.some(([key]) => key.includes('__ref_data__'));
      if (hasRefDataChanges) {
        this.onSubmit();
      }
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes.perimeter && !changes.perimeter.firstChange)
      || (changes.refreshKey && !changes.refreshKey.firstChange)
    ) {
      this.setUpRefDataByPerimeter();
    }
  }

  private setUpRefDataByPerimeter(): void {
    this.loading = true;

    this.refDataByPerimeter$ = combineLatest([
      this.referenceDataService.perimeterReferenceValues$({
        perimeter_ids: [this.perimeter.id, ...this.perimeter.sub_perimeters.map((subPerimeter) => subPerimeter.id)].join(','),
      }),
      this.scopeService.referenceFields$.pipe(idMapToArray()),
      this.usersService.allUsers$,
      this.scopeService.clusters$,
      this.scopeService.perimeterTypes$,
      this.scopeService.user$.pipe(map(({can_access_energy}) => can_access_energy)),
      this.trajectoryService.getPerimetersEnergyAreaBySite(this.perimeter.id),
      this.usersService.hasCurrentUserPermissions(['edit_reference_data']),
    ]).pipe(
      map(([
        perimetersReferenceDatas, referenceFields, users, clusters,
        perimeterTypes, isEnergyEnabled, areasByPerimeter, canEditRefData
      ]) => ([
        // Site
        {
          perimeter: this.perimeter,
          refData: this.getPerimeterRefData(
            this.perimeter,
            perimetersReferenceDatas,
            referenceFields,
            users,
            clusters,
            perimeterTypes,
            isEnergyEnabled,
            areasByPerimeter,
            canEditRefData,
          ),
          children: [],
        },
        // Level 1 perimeter
        ...this.perimeter.sub_perimeters.filter((perimeter1) => !perimeter1.level_parent).map((perimeter1) => ({
          perimeter: perimeter1,
          refData: this.getPerimeterRefData(
            perimeter1,
            perimetersReferenceDatas,
            referenceFields,
            users,
            clusters,
            perimeterTypes,
            isEnergyEnabled,
            areasByPerimeter,
            canEditRefData,
          ),
          // Level 2 perimeter
          children: this.perimeter.sub_perimeters.filter((perimeter2) =>
            perimeter2.level_parent === perimeter1.id
          ).map((perimeter2) => ({
            perimeter: perimeter2,
            refData: this.getPerimeterRefData(
              perimeter2,
              perimetersReferenceDatas,
              referenceFields,
              users,
              clusters,
              perimeterTypes,
              isEnergyEnabled,
              areasByPerimeter,
              canEditRefData,
            ),
            children: [],
          })),
        })),
      ])),
      tap((refDataByPerimeter) => {
        // Remove all form controls
        this.removeFormControls();
        // Create form controls
        refDataByPerimeter.forEach(({ refData, children }) => {
          this.createFormControl(refData);
          children.forEach(({ refData }) => this.createFormControl(refData));
        });

        // Save initial from values
        this.initialFormValues$.next(this.form.getRawValue());
        this.loading = false;
      }),

    );
  }

  private getPerimeterRefData(
    perimeter: KPI.Perimeter,
    perimetersReferenceDatas: KPI.PerimeterReferenceValue[],
    referenceFields: KPI.ReferenceField[],
    users: KPI.User[],
    clusters: IdMap<KPI.Cluster>,
    perimeterTypes: IdMap<KPI.PerimeterType>,
    isEnergyEnabled: boolean,
    areasByPerimeter: {[perimeterId: number]: number},
    canEditRefData: boolean,
  ): DisplayedPerimeterReferenceValue[]  {
    const datePipe = new DatePipe(this.i18nService.getCulture());
    const userPipe = new UsernamePipe(this.translate);
    return [
      ...this.getFirstPerimeterData(perimeter, clusters, perimeterTypes, isEnergyEnabled, areasByPerimeter, canEditRefData),
      ...referenceFields.sort((a, b) => a.order - b.order).filter((field) =>
        field.displayed_intelligence
        && (
          perimeter.is_monosite
            ? (!field.only_for_perimeter_types?.length || field.only_for_perimeter_types.includes(perimeter.perimeter_type))
            : field.available_for_sites
        )
      ).map((field) => {
        const refData = perimetersReferenceDatas.find((refData) =>
          refData.var_name === field.var_name && perimeter.id === refData.perimeter
        );
        return {
          var_name: `${perimeter.id}__ref_data__${field.var_name}${refData?.id ? `__${refData.id}` : ''}`,
          label: field.label,
          perimeter: perimeter.id,
          value: refData ? this.getRefDataValue(refData, field) : null,
          tooltip: this.translate.instant('Source') + ' : ' + (refData?.source ? refData.source : '') + '\n'
            + this.translate.instant('Author') + ' : ' + (refData?.author ? userPipe.transform(users.find((user) => user.id === refData.author)) : '') + '\n'
            + this.translate.instant('Date') + ' : ' + (refData?.date ? datePipe.transform(refData.date, 'longDate') : ''),
          disabled: field.is_locked || !canEditRefData || this.isSelectAndNoOptions(field),
          fieldObject: field,
        };
      }),
      ...this.getLastPerimeterData(perimeter, perimeterTypes),
    ];
  }

  // Perimeter data that are displayed before the reference data
  private getFirstPerimeterData(
    perimeter: KPI.Perimeter,
    clusters: IdMap<KPI.Cluster>,
    perimeterTypes: IdMap<KPI.PerimeterType>,
    isEnergyEnabled: boolean,
    areasByPerimeter: {[perimeterId: number]: number},
    _canEditRefData: boolean,
  ): DisplayedPerimeterReferenceValue[] {
    return [
      // Site or Perimter name
      {
        perimeter: perimeter.id,
        var_name: this.buildVarName(perimeter, 'name'),
        value: perimeter.name,
        label: perimeter.is_monosite
          ? this.translate.instant('Perimeter label')
          : this.translate.instant('Site label'),
        disabled: true,
      },
      // Site or Perimeter code
      ...(perimeter.code
        ? [{
            perimeter: perimeter.id,
            var_name: this.buildVarName(perimeter, 'code'),
            value: perimeter.code,
            label: this.translate.instant('Code')
              + ' '
              + (perimeter.is_monosite
                ? this.translate.instant('Perimeter').toLowerCase()
                : this.translate.instant('Site').toLowerCase()),
            disabled: true,
          }]
        : []
      ),
      // Site Cluster
      ...(!perimeter.is_monosite
        ? [{
            perimeter: perimeter.id,
            var_name: this.buildVarName(perimeter, 'cluster'),
            value: perimeter.cluster
              ? clusters[perimeter.cluster]?.name
              : '',
            label: this.translate.instant('Cluster'),
            disabled: true,
          }]
        : []
      ),
      // Perimeter creation year
      ...(perimeter.is_monosite
        ? [{
            perimeter: perimeter.id,
            var_name: this.buildVarName(perimeter, 'creation_year'),
            value: perimeter.creation_year,
            label: this.translate.instant('Last renovation work date'),
            disabled: true,
          }]
        : []
      ),
      // Perimeter energy area
      ...(perimeter.is_monosite
        && isEnergyEnabled
        && perimeterTypes[perimeter.perimeter_type]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING
        ? [{
            perimeter: perimeter.id,
            var_name: this.buildVarName(perimeter, 'energy_area'),
            value: (areasByPerimeter[perimeter.id] || perimeter.flooring_area)
              ? (areasByPerimeter[perimeter.id] || perimeter.flooring_area) + ' ' + this.translate.instant('m²')
              : '',
            label: this.translate.instant('Area for energy/carbon ratios'),
            disabled: true,
          }]
        : []
      ),
    ];
  }

  // Perimeter data that are displayed after the reference data
  private getLastPerimeterData(
    perimeter: KPI.Perimeter,
    perimeterTypes: IdMap<KPI.PerimeterType>,
  ): DisplayedPerimeterReferenceValue[] {
    return [
      // Perimeter type
      ...(perimeter.is_monosite
        ? [{
            perimeter: perimeter.id,
            var_name: this.buildVarName(perimeter, 'perimeter_type'),
            value: perimeter.cluster
              ? perimeterTypes[perimeter.perimeter_type]?.name
              : '',
            label: this.translate.instant('Perimeter type'),
            disabled: true,
          }]
        : []
      ),
    ];
  }

  private buildVarName = (perimeter: KPI.Perimeter, varName: string) => `${perimeter.id}__static__${varName}`;

  private createFormControl(
    refData: DisplayedPerimeterReferenceValue[],
  ): void {
    refData.forEach(({var_name, value, disabled}) => {
      this.form.addControl(
        var_name,
        new FormControl({ value, disabled }),
      );
    });
  }

  private removeFormControls(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.removeControl(key);
    });
  }

  public cancelChanges(): void {
    this.initialFormValues$.pipe(
      first(),
    ).subscribe((initialFormValues) => {
      this.form.reset(initialFormValues);
    });
  }

  public onSubmit(): void {
    this.setSaving();

    const changedValues = this.getChangedValues(this.form.value, this.initialFormValues$.value);

    // Ref data updates have key like "<perimeter_id>__ref_data__<var_name>__<refdata_id>"
    // Example: 1__ref_data__creation_date__118
    const refDataChangedValues = changedValues.filter(([key]) => key.includes('__ref_data__'));

    if (!refDataChangedValues?.length) {
      this.isSaving = false;
      this.saveToast.close();
      return;
    }

    this.manageRefDataUpdates(refDataChangedValues);

    // Perimeter data updates have key like "<perimeter_id>__static__<field_name>"
    // Example: 1__static__perimeter_type
    // const perimeterDataChangedValues = changedValues.filter(([key]) => key.includes('__static__'));

  }

  private manageRefDataUpdates(refDataChangedValues: [key: string, value: any][]): void {
    combineLatest(
      refDataChangedValues.map(([key, value]) => {
        const [perimeterId, _, varName, refDataId] = key.split('__');
        return combineLatest([
          this.scopeService.referenceFields$.pipe(idMapToArray()),
          this.scopeService.user$,
        ]).pipe(
          switchMap(([refFields, user]) => {
            const field = refFields.find((field) => field.var_name === varName);
            if (!!+refDataId) {
              // Ref data id exists, update the value
              return this.referenceDataService.updatePerimeterReferenceValue(
                +refDataId,
                {
                  author: user.id,
                  source: this.translate.instant('User'),
                  ...this.getRefDataValueAsParam(value, field)
                }
              );
            } else {
              // Ref data id doesn't exist, create a new one
              return this.referenceDataService.createPerimeterReferenceValue({
                perimeter: +perimeterId,
                field: field.id,
                year: new Date().getFullYear(),
                author: user.id,
                source: this.translate.instant('User'),
                ...this.getRefDataValueAsParam(value, field),
              });
            }
          }),
        );
      }),
    ).pipe(first()).subscribe({
      next: () => {
        this.loading = false;
        this.initialFormValues$.next(this.form.getRawValue());
        this.setSaved();
      },
      error: () => {
        this.loading = false;
        this.isSaving = false;
        this.saveToast.close();
        this.cdr.detectChanges();
      }
    });
  }

  // private managePerimeterDataUpdates(): void { }

  private getRefDataValueAsParam(value: any, field: KPI.ReferenceField): any {
    switch (field.data_type) {
      case KPI.ReferenceFieldDataType.DATA_TYPE_NUMBER:
        return { decimal_value: value };
      case KPI.ReferenceFieldDataType.DATA_TYPE_STRING:
        return { string_value: value };
      case KPI.ReferenceFieldDataType.DATA_TYPE_FK:
        return { fk_value: value };
      case KPI.ReferenceFieldDataType.DATA_TYPE_USER:
        return { user_value: value };
      default:
        return { string_value: value };
    }
  }

  private getRefDataValue(refData: KPI.PerimeterReferenceValue,  field: KPI.ReferenceField): string | number {
    switch (field.data_type) {
      case KPI.ReferenceFieldDataType.DATA_TYPE_FK:
      case KPI.ReferenceFieldDataType.DATA_TYPE_USER:
        return refData.filter_value;
      default:
        return refData.value;
    }
  }

  private isSelectAndNoOptions(field: KPI.ReferenceField): boolean {
    return (field.data_type === KPI.ReferenceFieldDataType.DATA_TYPE_FK || field.data_type === KPI.ReferenceFieldDataType.DATA_TYPE_USER)
      && !field.values?.length;
  }

  private getChangedValues(formValue: any, initialFormValues: any): any[] {
    return Object.entries(formValue).filter(([key, value]) =>
      initialFormValues[key] != value
      && !(!initialFormValues[key] && !value) // "" is considered as null, don't consider it as a change
    );
  }

  private setSaving(): void {
    this.isSaving = true;
    this.saveToast.showSaving();
    this.cdr.detectChanges();
  }

  private setSaved(): void {
    this.isSaving = false;
    this.saveToast.showSaved();
    this.cdr.detectChanges();
  }

  public ngOnDestroy(): void {
    if (this.autosaveSubscription) {
      this.autosaveSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
